// O Editor do Mentor, aba Clube (Círculo, parte 5, 15/10/2026). O desenho é
// docs/circulo-mentor/prototipo/editor-do-mentor.html (formClube, resumoClube,
// viewClubePainel).
//
// Por série: o clube que a aba Clube da página mostra. Dois caminhos —
// "Criar um clube aberto" (nome e descrição dele; o clube nasce pela rota de
// sempre do app, POST /api/clubs, com o livro da série) e "Ligar a um que já
// existe" (só os clubes de que ele é dono). Desligar volta à regra padrão: o
// clube aberto dele (server/circulo/abas.js, clubeDoMentor). API:
// server/circulo/editor-clube.js.
//
// REGRA DO WESLEY: o Mentor monta tudo. O nome do clube começa vazio e nunca
// vem do nome da série nem do livro. A descrição é opcional: fica fora do
// assistente e, na aba, em "Mais detalhes" recolhido (TELA LIMPA, 18/10).
// Mudar a descrição depois é dentro do próprio clube.
//
// No assistente "Montar meu Círculo" (prop `assistente`), a mesma tela é o
// passo do clube, preso à série do assistente: o Avançar do rodapé do passo
// cria o clube aberto (se o nome foi escrito) e segue; ligar a um que já
// existe grava na hora, como na aba.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { Ic, type RodapePasso } from './editor-comum';

type Tipo = 'novo' | 'existente';
const LIM = { nome: 80, descricao: 280 };
const contar = (s: string) => Array.from(s).length;
const membros = (n: number | null) => (n == null ? '' : n === 1 ? ' · 1 membro' : ` · ${n} membros`);

// Uma linha para a Visão geral: o que a aba Clube da página mostra nesta série.
export function resumoClube(s: any): string {
  if (s?.vinculo === 'serie' && s.clube) return `Ligado a ${s.clube.nome} · clube ${s.clube.aberto ? 'aberto' : 'fechado'}`;
  if (s?.vinculo === 'dono' && s.clube) return `${s.clube.nome} · o seu clube aberto (padrão)`;
  return 'sem clube';
}

export const AbaClube: React.FC<{
  session: any;
  onNaoEMentor: () => void;
  onAbrirClube: (clubId: string) => void;
  onIrSerie: () => void;
  onMudou?: (d: any) => void; // o resumo da Visão geral
  assistente?: { serieId: string | null; rodape: RodapePasso; onAvancou: () => void };
}> = ({ session, onNaoEMentor, onAbrirClube, onIrSerie, onMudou, assistente }) => {
  const [d, setD] = useState<any>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [serieId, setSerieId] = useState<string | null>(null);
  const [tipo, setTipo] = useState<Tipo>('novo');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [salvo, setSalvo] = useState<string | null>(null);
  const [maisAberto, setMaisAberto] = useState(false);
  const naoEMentor = useRef(onNaoEMentor);
  naoEMentor.current = onNaoEMentor;
  const mudouRef = useRef(onMudou);
  mudouRef.current = onMudou;
  const serieFixa = useRef(assistente?.serieId || null);

  const tratar403 = (e: any) => { if (e?.status === 403 || e?.status === 401) { naoEMentor.current(); return true; } return false; };
  const erroDaApi = (e: any, padrao = 'Não deu para salvar agora. Confira a conexão e tente de novo.') =>
    (e?.status === 400 || e?.status === 404 ? (e?.dados?.error || e.message) : padrao);

  // Chegou a resposta da API: a série escolhida segue a mesma (ou a 1ª).
  const aplicar = useCallback((x: any, avisar = true) => {
    setD(x);
    const existe = (id: string | null) => !!id && x.series?.some((s: any) => s.id === id);
    setSerieId((atual) => (existe(serieFixa.current) ? serieFixa.current : existe(atual) ? atual : x.series?.[0]?.id || null));
    if (avisar) mudouRef.current?.(x);
  }, []);

  const carregar = useCallback(async () => {
    setFalha(null);
    try { aplicar(await api.getClubesEditor(session)); } catch (e: any) {
      if (tratar403(e)) return;
      setFalha('Não deu para abrir os clubes agora. Confira a conexão e tente de novo.');
    }
  }, [session, aplicar]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (!salvo) return; const t = setTimeout(() => setSalvo(null), 4000); return () => clearTimeout(t); }, [salvo]);

  const serie = d?.series?.find((s: any) => s.id === serieId) || null;
  // Cada série abre no caminho que ela já usa: ligada a um clube, "existente".
  useEffect(() => {
    if (!serieId) return;
    setTipo(serie?.vinculo === 'serie' ? 'existente' : 'novo');
    setErros({}); setSalvo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serieId]);

  // O clique na série troca a série e o caminho no mesmo render (o efeito acima
  // cobre a 1ª abertura; sozinho, ele deixa um quadro com o caminho da outra).
  const trocarSerie = (s: any) => {
    setSerieId(s.id);
    setTipo(s.vinculo === 'serie' ? 'existente' : 'novo');
    setErros({}); setSalvo(null);
  };

  const lim = { ...LIM, ...(d?.limites || {}) };

  const ligar = async (clubId: string | null) => {
    if (!serie || ocupado) return;
    setOcupado(clubId || 'desligar'); setErros({}); setSalvo(null);
    try {
      aplicar(await api.ligarClubeEditor(session, serie.id, clubId));
      setSalvo(clubId ? 'Clube ligado à série.' : 'Desligado: vale a regra padrão.');
      if (!clubId) setTipo('existente');
    } catch (e: any) {
      if (tratar403(e)) return;
      setErros({ _geral: erroDaApi(e) });
    } finally { setOcupado(null); }
  };

  // Devolve se o clube foi criado e ligado à série.
  const criar = async (): Promise<boolean> => {
    if (!serie || ocupado) return false;
    const n = nome.trim();
    const desc = descricao.trim();
    if (!n) { setErros({ nome: 'Escreva o nome do clube.' }); return false; }
    if (contar(n) > lim.nome) { setErros({ nome: `O nome tem ${contar(n)} caracteres; o limite é ${lim.nome}.` }); return false; }
    if (contar(desc) > lim.descricao) { setErros({ descricao: `A descrição tem ${contar(desc)} caracteres; o limite é ${lim.descricao}.` }); return false; }
    setOcupado('criar'); setErros({}); setSalvo(null);
    // A criação de sempre do app (POST /api/clubs): aberto, leitura em grupo,
    // com o livro da série como o livro atual do clube.
    let clube: any;
    try {
      const l = serie.livro;
      clube = await api.createClub(session, {
        name: n,
        description: desc || null,
        mode: 'group-read',
        privacy: 'open',
        ...(l?.titulo ? { book: { title: l.titulo, author: l.autor || '', totalPages: l.paginas || null, coverUrl: serie.cover_url || null } } : {}),
      });
    } catch (e: any) {
      if (tratar403(e)) { setOcupado(null); return false; }
      setErros({ nome: e?.status === 400 ? (e?.dados?.error || e.message) : 'Não deu para criar o clube agora. Confira a conexão e tente de novo.' });
      setOcupado(null);
      return false;
    }
    try {
      aplicar(await api.ligarClubeEditor(session, serie.id, clube.id));
      setNome(''); setDescricao(''); setTipo('existente');
      setSalvo('Clube criado e ligado à série.');
      return true;
    } catch (e: any) {
      if (tratar403(e)) return false;
      setErros({ _geral: 'O clube foi criado, mas não deu para ligá-lo à série. Escolha-o em "Ligar a um que já existe".' });
      carregar();
      return false;
    } finally { setOcupado(null); }
  };

  // O Avançar do assistente: com o nome escrito, cria o clube aberto; sem ele,
  // segue com o clube que a série já mostra (ligado ou o padrão).
  const vaiCriar = tipo === 'novo' && !!nome.trim();
  const avancarPasso = async () => {
    if (!assistente || !serie || ocupado) return;
    if (vaiCriar) { if (!(await criar())) return; } else if (!serie.vinculo) return;
    assistente.onAvancou();
  };

  const contador = (n: number, l: number, k: string) =>
    <span className="ced-cnt" data-cnt={k} data-estado={n > l ? 'passou' : n >= l * 0.92 ? 'perto' : ''}>{n}/{l}</span>;
  const erroDe = (k: string) => erros[k] ? <p className="ced-erro" role="alert" data-erro={k}>{erros[k]}</p> : null;
  const abrirClube = (c: any) => (
    <a className="ccl-abrir" href={c.endereco} data-acao="abrir-clube" data-abrir-clube={c.id}
      onClick={(e) => { e.preventDefault(); onAbrirClube(c.id); }}>abrir o clube</a>
  );

  const cabeca = (
    <>
      <span className="ced-eyebrow">Círculo · editor</span>
      <h1 className="ced-titulo">Clube</h1>
    </>
  );

  if (falha) {
    return <section className="ced-clube" data-vista-clube="">{cabeca}
      <div className="ced-aviso"><p>{falha}</p><button className="ced-btn ced-btn-2" onClick={carregar}>Tentar de novo</button></div></section>;
  }
  if (!d) return <section className="ced-clube" data-vista-clube="">{cabeca}<p className="ced-nota" aria-busy="true">Abrindo os clubes…</p></section>;
  if (!d.series?.length || !serie) {
    return (
      <section className="ced-clube" data-vista-clube="" data-clube-sem-serie="">
        {!assistente && cabeca}
        <p className="ced-nota">Crie uma série primeiro.</p>
        <div className="ced-linha ccl-topo"><button className="ced-btn ced-btn-2" data-acao="ir-serie" onClick={onIrSerie}>{Ic.serie}<span>Ir para Séries</span></button></div>
        {assistente?.rodape({ pronto: false, ocupado: false, avancar: () => {} })}
      </section>
    );
  }

  const clubes: any[] = d.clubes || [];
  const nN = contar(nome), nD = contar(descricao);
  const ligadoId = serie.vinculo === 'serie' ? serie.club_id : null;

  return (
    <section className="ced-clube" data-vista-clube="" data-clube-serie={serie.id}>
      {!assistente && cabeca}

      {!assistente && d.series.length > 1 && (
        <>
          <div className="ced-secao-titulo"><h3>Série</h3></div>
          <div className="ccl-series" role="tablist" aria-label="Série">
            {d.series.map((s: any) => (
              <button key={s.id} role="tab" aria-selected={s.id === serie.id} data-serie-clube={s.id} onClick={() => trocarSerie(s)}>
                {s.titulo || s.livro?.titulo}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="ccl-agora" data-clube-agora="" data-vinculo={serie.vinculo || 'nenhum'}>
        <span className="ccl-agora-ic">{Ic.clube}</span>
        <div className="ccl-agora-tx">
          {serie.vinculo === 'serie' && serie.clube ? (
            <p>Ligado a <b>{serie.clube.nome}</b><small>clube {serie.clube.aberto ? 'aberto' : 'fechado'}{membros(serie.clube.membros)}</small></p>
          ) : serie.vinculo === 'dono' && serie.clube ? (
            <p><b>{serie.clube.nome}</b><small>o seu clube aberto (padrão){membros(serie.clube.membros)}</small></p>
          ) : (
            <p>Sem clube</p>
          )}
          <div className="ced-linha">
            {serie.clube && abrirClube(serie.clube)}
            {serie.vinculo === 'serie' && (
              <button className="ced-btn ced-btn-q ced-btn-sm" data-acao="desligar-clube" disabled={!!ocupado} onClick={() => ligar(null)}>
                {ocupado === 'desligar' ? 'Desligando…' : 'Desligar'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="ces-modos ccl-tipo" role="tablist" aria-label="Clube da série">
        <button role="tab" aria-selected={tipo === 'novo'} data-clube-tipo="novo" onClick={() => { setTipo('novo'); setErros({}); }}>Criar um clube aberto</button>
        <button role="tab" aria-selected={tipo === 'existente'} data-clube-tipo="existente" onClick={() => { setTipo('existente'); setErros({}); }}>Ligar a um que já existe</button>
      </div>

      {tipo === 'novo' ? (
        <div className="ccl-novo" data-clube-novo="">
          <div className="ced-campo" data-campo="nome" data-invalido={erros.nome ? '1' : undefined}>
            <label htmlFor="ccl-nome"><span>Nome do clube</span>{contador(nN, lim.nome, 'nome')}</label>
            <input id="ccl-nome" type="text" value={nome}
              onChange={(e) => { setNome(e.target.value); if (erros.nome) setErros({}); }} />
            {erroDe('nome')}
          </div>
          {!assistente && (
            <details className="ced-mais" data-mais-detalhes="" open={maisAberto || !!erros.descricao}
              onToggle={(e) => setMaisAberto((e.currentTarget as HTMLDetailsElement).open)}>
              <summary>{Ic.descer}<span>Mais detalhes</span></summary>
              <div className="ced-campo" data-campo="descricao" data-invalido={erros.descricao ? '1' : undefined}>
                <label htmlFor="ccl-descricao"><span>Descrição</span>{contador(nD, lim.descricao, 'descricao')}</label>
                <textarea id="ccl-descricao" rows={3} value={descricao}
                  onChange={(e) => { setDescricao(e.target.value); if (erros.descricao) setErros({}); }} />
                {erroDe('descricao')}
              </div>
            </details>
          )}
          {assistente ? (
            serie.vinculo ? <p className="ced-nota" data-clube-ao-avancar="">Sem nome, segue com o clube atual.</p> : null
          ) : (
            <div className="ced-linha ccl-acoes">
              <button className="ced-btn ced-btn-1" data-acao="criar-clube" disabled={!nome.trim() || !!ocupado || nN > lim.nome || nD > lim.descricao} onClick={criar}>
                {ocupado === 'criar' ? 'Criando…' : 'Criar e ligar à série'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="ccl-existentes" data-clube-existentes="">
          <div className="ced-secao-titulo"><h3>Seus clubes</h3></div>
          {!clubes.length ? (
            <p className="ced-nota ccl-vazio" data-clubes-vazio="">Nenhum clube seu ainda.</p>
          ) : (
            <div className="ccl-lista">
              {clubes.map((c) => (
                <button key={c.id} className="ccl-opcao" aria-pressed={ligadoId === c.id} data-clube-opcao={c.id}
                  disabled={!!ocupado} onClick={() => (ligadoId === c.id ? undefined : ligar(c.id))}>
                  <span className="ccl-opcao-ic">{Ic.clube}</span>
                  <span className="ccl-opcao-tx"><b>{c.nome}</b><small>{c.aberto ? 'aberto' : 'fechado'}{membros(c.membros)}</small></span>
                  <span className="ccl-check" aria-hidden="true">{ligadoId === c.id ? Ic.ok : null}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ccl-estado" aria-live="polite">
        {salvo ? <span className="ced-salvo" role="status" data-salvo="">{Ic.ok}{salvo}</span> : erroDe('_geral')}
      </div>
      {assistente?.rodape({
        pronto: vaiCriar ? nN <= lim.nome && nD <= lim.descricao : !!serie.vinculo,
        ocupado: !!ocupado,
        avancar: avancarPasso,
      })}
    </section>
  );
};
